// Чистая математика вкладки «период» в дневнике (Diary.vue, с 19.10.2026):
// даты диапазона, укрупнение ряда по дням в недели и месяцы, дни в норме,
// тенденция веса и ожидаемый путь. Без Vue и без запросов — проверяется
// node scripts/test-period-charts.js.

export const DAY_MS = 24 * 3600 * 1000

export function addDays(ymd, n) {
  const d = new Date(ymd + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

export function daysBetween(from, to) {
  return Math.round((new Date(to + 'T00:00:00Z') - new Date(from + 'T00:00:00Z')) / DAY_MS)
}

// Понедельник недели, в которую попал день
export function mondayOf(ymd) {
  const d = new Date(ymd + 'T00:00:00Z')
  const shift = (d.getUTCDay() + 6) % 7
  d.setUTCDate(d.getUTCDate() - shift)
  return d.toISOString().slice(0, 10)
}

// Шкала графика по длине периода: до 35 дней — дни, до года — недели, дальше месяцы
export function scaleFor(from, to) {
  const n = daysBetween(from, to) + 1
  if (n <= 35) return 'day'
  if (n <= 366) return 'week'
  return 'month'
}

const MONTHS_SHORT = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']
const MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']

export function shortDate(ymd) {
  const [, m, d] = ymd.split('-').map(Number)
  return `${d} ${MONTHS_SHORT[m - 1]}`
}

// «1 сентября» — подпись круглой даты на графике
export function longDate(ymd) {
  const [, m, d] = ymd.split('-').map(Number)
  return `${d} ${MONTHS_GEN[m - 1]}`
}

// Заголовок вкладки: в одном месяце месяц целиком («5–18 октября»), в разных —
// по три буквы, чтобы «Всего за …» влезало в строку («19 фев – 26 окт»)
export function headerRange(from, to) {
  const [y1, m1, d1] = from.split('-').map(Number)
  const [y2, m2, d2] = to.split('-').map(Number)
  if (y1 === y2 && m1 === m2) return d1 === d2 ? `${d1} ${MONTHS_GEN[m1 - 1]}` : `${d1}–${d2} ${MONTHS_GEN[m1 - 1]}`
  const yy1 = y1 !== y2 ? ` ${y1}` : '', yy2 = y1 !== y2 ? ` ${y2}` : ''
  return `${d1} ${MONTHS_SHORT[m1 - 1]}${yy1} – ${d2} ${MONTHS_SHORT[m2 - 1]}${yy2}`
}

// Подписи дат под графиком — «круглые»: 1-е и 15-е числа внутри ряда
// (решение владельца 27.10.2026). Круглых меньше двух — первая и последняя.
export function tickIndexes(dates) {
  const n = dates.length
  if (!n) return []
  const round = []
  dates.forEach((d, i) => { const day = Number(d.slice(8, 10)); if (day === 1 || day === 15) round.push(i) })
  if (round.length >= 2) return round
  return n === 1 ? [0] : [0, n - 1]
}

// Подпись селектора периода — всегда коротко, чтобы «Аналитика» и селектор
// встали в одну строку на любом телефоне: «5–18 окт», «19 фев – 26 окт»
export function shortRange(from, to) {
  const [y1, m1, d1] = from.split('-').map(Number)
  const [y2, m2, d2] = to.split('-').map(Number)
  if (y1 === y2 && m1 === m2) return d1 === d2 ? `${d1} ${MONTHS_SHORT[m1 - 1]}` : `${d1}–${d2} ${MONTHS_SHORT[m1 - 1]}`
  const yy1 = y1 !== y2 ? ` ${String(y1).slice(2)}` : '', yy2 = y1 !== y2 ? ` ${String(y2).slice(2)}` : ''
  return `${d1} ${MONTHS_SHORT[m1 - 1]}${yy1} – ${d2} ${MONTHS_SHORT[m2 - 1]}${yy2}`
}

/** Последняя серия дней с записями подряд (конец — последний день не позже yesterday) или null */
export function lastStreak(trackedDays, yesterday) {
  const days = [...new Set(trackedDays)].filter(d => d <= yesterday).sort()
  if (!days.length) return null
  let end = days[days.length - 1], start = end
  for (let i = days.length - 2; i >= 0; i--) {
    if (days[i] === addDays(start, -1)) start = days[i]
    else break
  }
  return { from: start, to: end }
}

/**
 * Диапазон по умолчанию для вкладки «аналитика» (решение владельца 27.10.2026):
 * последняя серия дней с записями подряд, если в ней от 7 дней; короче —
 * последние 30 дней (от первого дня дневника, если он позже); дней с
 * записями меньше двух — null, вкладка показывает пример. Конец серии —
 * последний день с записями не позже вчера.
 */
export function defaultRange(trackedDays, yesterday) {
  const days = [...new Set(trackedDays)].filter(d => d <= yesterday).sort()
  if (days.length < 2) return null
  const streak = lastStreak(days, yesterday)
  if (daysBetween(streak.from, streak.to) + 1 >= 7) return streak
  const from = days[0] > addDays(yesterday, -29) ? days[0] : addDays(yesterday, -29)
  return { from, to: yesterday }
}

// «11 сентября – 20 октября», в одном месяце «1–20 октября»
export function rangeLabel(from, to) {
  const [y1, m1, d1] = from.split('-').map(Number)
  const [y2, m2, d2] = to.split('-').map(Number)
  if (y1 === y2 && m1 === m2) return d1 === d2 ? `${d1} ${MONTHS_GEN[m1 - 1]}` : `${d1}–${d2} ${MONTHS_GEN[m1 - 1]}`
  const yy = y1 !== y2 ? ` ${y1}` : ''
  return `${d1} ${MONTHS_GEN[m1 - 1]}${yy} – ${d2} ${MONTHS_GEN[m2 - 1]}${y1 !== y2 ? ` ${y2}` : ''}`
}

export function dayWord(n) {
  const a = n % 10, b = n % 100
  if (b >= 11 && b <= 14) return 'дней'
  if (a === 1) return 'день'
  if (a >= 2 && a <= 4) return 'дня'
  return 'дней'
}

/**
 * Укрупнить ряд по дням. days — [{ day, <key>: value }] только за дни с
 * записями; from..to — границы периода. Возвращает корзины
 * [{ from, to, label, value, n, days: [day…] }]: value — среднее за дни с
 * записями в корзине (а не за календарные дни: пропущенный день — не ноль
 * калорий, а отсутствие записи), n — сколько таких дней, пустые корзины
 * остаются с value null, чтобы ось не рвалась.
 */
export function bucketSeries(days, key, from, to, scale = scaleFor(from, to)) {
  const byDay = new Map(days.map(d => [d.day, d]))
  const buckets = []
  if (scale === 'day') {
    for (let d = from; d <= to; d = addDays(d, 1)) {
      const row = byDay.get(d)
      const v = row && row[key] != null ? Number(row[key]) : null
      buckets.push({ from: d, to: d, label: shortDate(d), value: v, complete: row?.complete, n: v != null ? 1 : 0, days: v != null ? [d] : [] })
    }
    return buckets
  }
  let cur = scale === 'week' ? mondayOf(from) : from.slice(0, 8) + '01'
  while (cur <= to) {
    const next = scale === 'week' ? addDays(cur, 7) : addDays(cur.slice(0, 8) + '01', 32).slice(0, 8) + '01'
    const bFrom = cur < from ? from : cur
    const bTo = addDays(next, -1) > to ? to : addDays(next, -1)
    let sum = 0, n = 0
    const inDays = []
    for (let d = bFrom; d <= bTo; d = addDays(d, 1)) {
      const row = byDay.get(d)
      if (row && row.complete !== false && row[key] != null) { sum += Number(row[key]); n++; inDays.push(d) }
    }
    buckets.push({
      from: bFrom, to: bTo,
      label: scale === 'week' ? shortDate(bFrom) : MONTHS_SHORT[Number(cur.slice(5, 7)) - 1],
      value: n ? Math.round(sum / n * 10) / 10 : null, n, days: inDays,
    })
    cur = next
  }
  return buckets
}

// Сколько дней ряда попали в норму: lower..upper, только upper — «не выше»,
// только lower — «не ниже». Возвращает { ok, total }.
export function daysInNorm(days, key, { lower = null, upper = null } = {}) {
  let ok = 0, total = 0
  for (const d of days) {
    if (d.complete === false) continue
    const v = d[key]
    if (v == null) continue
    total++
    if ((lower == null || v >= lower) && (upper == null || v <= upper)) ok++
  }
  return { ok, total }
}

/**
 * Тенденция веса: взвешенное среднее замеров в ±7 днях от каждого дня периода,
 * веса убывают от центра. От трёх замеров. points — [{ day, kg }] (могут быть
 * и до from — для левого края), результат — [{ day, kg }] по дням from..to,
 * где рядом есть хотя бы два замера в ±7 днях или три в ±14.
 */
export function weightTrend(points, from, to) {
  if (points.length < 3) return []
  const idx = (d) => daysBetween(from, d)
  const pts = points.map(p => ({ i: idx(p.day), kg: Number(p.kg) })).filter(p => Number.isFinite(p.kg))
  const out = []
  const n = daysBetween(from, to)
  for (let i = 0; i <= n; i++) {
    const near7 = pts.filter(p => Math.abs(p.i - i) <= 7).length
    const near14 = pts.filter(p => Math.abs(p.i - i) <= 14).length
    if (near7 < 2 && near14 < 3) continue
    const radius = near7 >= 2 ? 7 : 14
    let s = 0, w = 0
    for (const p of pts) {
      const dd = Math.abs(p.i - i)
      if (dd > radius) continue
      const wt = 1 - dd / (radius + 1)
      s += p.kg * wt; w += wt
    }
    if (w > 0) out.push({ day: addDays(from, i), kg: Math.round(s / w * 100) / 100 })
  }
  return out
}

// Скорость по тенденции: наклон за последние три недели, кг в неделю
export function weeklyRate(trend) {
  if (trend.length < 8) return null
  const seg = trend.slice(-21)
  const n = seg.length
  let sx = 0, sy = 0, sxx = 0, sxy = 0
  seg.forEach((p, j) => { sx += j; sy += p.kg; sxx += j * j; sxy += j * p.kg })
  const den = n * sxx - sx * sx
  if (!den) return null
  return Math.round((n * sxy - sx * sy) / den * 7 * 100) / 100
}

/**
 * Ожидаемый путь при дефиците deficitKcal в день (Hall et al., 2011,
 * линеаризованная модель): dW/dt = −(D − K·ΔW)/RHO, каждый потерянный
 * килограмм снижает расход на K = 22 ккал/день, килограмм тканей стоит
 * RHO = 7700 ккал. Путь начинается от anchorKg в день 0 и идёт на n дней.
 * Без дефицита (≤ 0) пути нет.
 */
export function expectedPath(anchorKg, deficitKcal, n) {
  if (!(deficitKcal > 0) || !(anchorKg > 0) || !(n > 0)) return []
  const K = 22, RHO = 7700
  const out = []
  for (let t = 0; t <= n; t++) {
    const lost = (deficitKcal / K) * (1 - Math.exp(-K * t / RHO))
    out.push(Math.round((anchorKg - lost) * 100) / 100)
  }
  return out
}
